import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Camera, Shield, Home, Wifi, Server, Wrench, Headphones, Zap, CheckCircle, Clock, Settings } from "lucide-react";

function useInView() {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("visible");
        });
      },
      { threshold: 0.1 }
    );
    const el = ref.current;
    if (el) {
      el.querySelectorAll(".animate-on-scroll").forEach((c) => observer.observe(c));
      if (el.classList.contains("animate-on-scroll")) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);
  return ref;
}

const services = [
  {
    icon: Camera,
    title: "Professional CCTV Systems",
    desc: "Complete surveillance solutions from residential to enterprise-grade installations.",
    features: ["4K Ultra HD Cameras", "Night Vision & ColorVu", "Smart Motion Detection", "Remote Mobile Viewing", "Cloud & Local Storage", "Up to 32 Camera Support"],
  },
  {
    icon: Shield,
    title: "Alarm Systems",
    desc: "Advanced intrusion detection and alarm systems for comprehensive protection.",
    features: ["Motion Sensors", "Door/Window Sensors", "Smoke Detectors", "Panic Buttons", "Outdoor Sirens", "Mobile App Alerts"],
  },
  {
    icon: Home,
    title: "Smart Home Automation",
    desc: "Transform your living space with intelligent home technology.",
    features: ["Smart Lighting Control", "Automated Curtains", "Smart Locks & Access", "Voice Control Integration", "Energy Monitoring", "Centralized App Control"],
  },
  {
    icon: Wifi,
    title: "Starlink Internet",
    desc: "High-speed satellite internet installation and optimization.",
    features: ["Professional Installation", "Signal Optimization", "Router Configuration", "Coverage Extension", "Speed Testing", "Ongoing Support"],
  },
  {
    icon: Server,
    title: "Enterprise Networking",
    desc: "Scalable network infrastructure for businesses of all sizes.",
    features: ["Structured Cabling", "Enterprise WiFi", "Managed Switches", "Fiber Networking", "Access Points", "Rack Installation"],
  },
  {
    icon: Wrench,
    title: "Installation & Configuration",
    desc: "Expert setup of all security and technology systems.",
    features: ["Site Assessment", "System Design", "Professional Wiring", "Equipment Setup", "Testing & QA", "Documentation"],
  },
  {
    icon: Clock,
    title: "Annual Maintenance Contracts",
    desc: "Keep your systems running at peak performance year-round.",
    features: ["Regular Inspections", "Preventive Maintenance", "Firmware Updates", "System Health Reports", "Priority Support", "Discounted Parts"],
  },
  {
    icon: Headphones,
    title: "24/7 Technical Support",
    desc: "Round-the-clock expert assistance whenever you need it.",
    features: ["Remote Troubleshooting", "On-Site Support", "Phone & WhatsApp", "Emergency Response", "System Monitoring", "Software Updates"],
  },
];

export default function Services() {
  const ref = useInView();

  return (
    <div ref={ref}>
      {/* Hero */}
      <section className="pt-32 pb-20 section-padding bg-brand-gray">
        <div className="container-custom">
          <div className="max-w-3xl">
            <p className="animate-on-scroll text-sm font-semibold text-brand-orange uppercase tracking-wider mb-3">Our Services</p>
            <h1 className="animate-on-scroll stagger-1 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Complete Technology
              <br /><span className="gradient-text">Solutions</span>
            </h1>
            <p className="animate-on-scroll stagger-2 text-lg text-brand-muted leading-relaxed">
              From professional CCTV installations to smart home automation, we provide end-to-end solutions tailored to your needs.
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="space-y-12">
            {services.map((s, i) => (
              <div key={i} className={`animate-on-scroll stagger-${(i % 4) + 1} bg-white rounded-3xl p-8 lg:p-12 shadow-card border border-brand-border/50 overflow-hidden hover:shadow-card-hover transition-all duration-500`}>
                <div className="grid lg:grid-cols-2 gap-8 items-start">
                  <div>
                    <div className="w-14 h-14 bg-brand-orange/10 rounded-2xl flex items-center justify-center mb-5">
                      <s.icon size={28} className="text-brand-orange" />
                    </div>
                    <h3 className="font-display text-2xl font-bold mb-3">{s.title}</h3>
                    <p className="text-brand-muted leading-relaxed mb-6">{s.desc}</p>
                    <Link to="/contact" className="inline-flex items-center gap-2 text-brand-orange font-semibold hover:gap-3 transition-all">
                      Get Quote <ArrowRight size={16} />
                    </Link>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {s.features.map((f, j) => (
                      <div key={j} className="flex items-center gap-2 bg-brand-gray/50 rounded-xl px-4 py-3">
                        <CheckCircle size={14} className="text-brand-orange shrink-0" />
                        <span className="text-sm font-medium">{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section-padding bg-brand-gray">
        <div className="container-custom">
          <div className="text-center mb-16">
            <p className="animate-on-scroll text-sm font-semibold text-brand-orange uppercase tracking-wider mb-3">Our Process</p>
            <h2 className="animate-on-scroll stagger-1 font-display text-3xl sm:text-4xl font-bold tracking-tight">
              How We Work
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Consultation", desc: "Free site visit and requirements assessment" },
              { step: "02", title: "Design", desc: "Custom solution design and proposal" },
              { step: "03", title: "Installation", desc: "Professional installation and configuration" },
              { step: "04", title: "Support", desc: "Ongoing maintenance and 24/7 support" },
            ].map((p, i) => (
              <div key={i} className={`animate-on-scroll stagger-${i + 1} text-center`}>                
                <div className="text-5xl font-bold text-brand-orange/20 mb-4">{p.step}</div>
                <h3 className="font-semibold text-lg mb-2">{p.title}</h3>
                <p className="text-sm text-brand-muted">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-brand-dark text-white text-center">
        <div className="container-custom">
          <h2 className="animate-on-scroll font-display text-3xl sm:text-4xl font-bold tracking-tight mb-6">
            Need a Custom Solution?
          </h2>
          <p className="animate-on-scroll stagger-1 text-lg text-white/60 mb-10 max-w-2xl mx-auto">
            Every project is unique. Let us design the perfect security and technology solution for your space.
          </p>
          <div className="animate-on-scroll stagger-2 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="btn-primary">
              Get Free Consultation
              <ArrowRight size={18} className="ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
